import { useEffect, useRef } from 'react'
import http from '../http'

let refreshTokenPromise: Promise<string | undefined> | null = null

export const useHttpInterceptorEffect = ({
  refreshToken,
  onError,
}: {
  refreshToken: () => Promise<string | undefined>
  onError: ({ status }: { status: 'no_access' | 'token_invalid' }) => void
}) => {
  const isRefreshing = useRef(false)

  useEffect(() => {
    // Response interceptor
    const interceptor = http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Handle token expiration
        if (error.response && error.response.status === 401) {
          console.log('401', error.response.data)
          if (error?.response?.data?.message?.toLowerCase() === 'no access') {
            onError({ status: 'no_access' })
            return Promise.reject(error)
          }
          if (originalRequest._retry) {
            console.log('RETRY')
            onError({ status: 'token_invalid' })
            return Promise.reject(error)
          }

          originalRequest._retry = true

          try {
            let token
            if (isRefreshing.current) {
              token = await refreshTokenPromise
            } else {
              isRefreshing.current = true
              refreshTokenPromise = refreshToken()
              token = await refreshTokenPromise
              isRefreshing.current = false
              refreshTokenPromise = null
            }
            if (!token) {
              console.log('token is null')
              onError({ status: 'token_invalid' })
              return Promise.reject(error)
            }

            http.config({ token })

            originalRequest.headers.Authorization = `Bearer ${token}`

            return http(originalRequest)
          } catch (refreshError) {
            isRefreshing.current = false
            refreshTokenPromise = null
            console.log('refresh error', refreshError)
            onError({ status: 'token_invalid' })
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )

    // Cleanup
    return () => {
      http.interceptors.response.eject(interceptor)
    }
  }, [refreshToken, onError])
}
